import { useState } from "react";
import { BeatShell } from "@/components/nutrition/NutritionParts";
import { gradeChips, beatMax } from "../beatUtils";
import { Briefing } from "./Shared";
import { ShieldAlert, Check, X } from "lucide-react";

// Sterile-field diagram: tap the hotspots that break sterility. Graded like a
// multi (catch the breaches, avoid flagging the sterile actions).
export function SterileFieldBeat({ beat, eyebrow, showBriefing, c, accent, onDone }) {
  const [sel, setSel] = useState(new Set());
  const [res, setRes] = useState(null);
  const checked = !!res;
  const toggle = (i) => {
    if (checked) return;
    setSel((prev) => { const n = new Set(prev); n.has(i) ? n.delete(i) : n.add(i); return n; });
  };
  const onPrimary = () => {
    if (!checked) {
      const g = gradeChips(beat.spots, sel, beat.full ?? 20);
      setRes({ earned: g.pts, max: beatMax(beat), detail: `Caught ${g.caught}/${g.total} breaches, ${g.falseTaps} false`, key: beat.key, label: beat.label });
    } else onDone(res);
  };

  return (
    <BeatShell
      Icon={ShieldAlert}
      eyebrow={eyebrow}
      title={beat.title}
      subtitle={beat.prompt}
      primaryLabel={checked ? (beat.last ? "See debrief →" : "Continue →") : "Check the field"}
      onPrimary={onPrimary}
      primaryTestId={`${beat.key}-primary-btn`}
      accent={accent}
    >
      {showBriefing && <Briefing c={c} />}

      <div className="relative rounded-xl overflow-hidden border-2 border-teal-200" data-testid="sterile-field" style={{ aspectRatio: "16 / 9", background: "linear-gradient(160deg,#0f766e 0%,#115e59 60%,#134e4a 100%)" }}>
        {/* scene */}
        <svg viewBox="0 0 320 180" className="absolute inset-0 w-full h-full" preserveAspectRatio="xMidYMid slice">
          {/* draped surgical table */}
          <rect x="70" y="96" width="180" height="58" rx="6" fill="#5eead4" opacity="0.85" />
          <rect x="70" y="96" width="180" height="10" rx="5" fill="#99f6e4" />
          {/* patient mound under drape */}
          <ellipse cx="160" cy="112" rx="50" ry="12" fill="#2dd4bf" opacity="0.5" />
          {/* Mayo stand / instrument tray */}
          <rect x="250" y="70" width="58" height="30" rx="4" fill="#5eead4" opacity="0.85" />
          <rect x="276" y="100" width="4" height="50" fill="#0d9488" />
          {/* gowned surgeon */}
          <circle cx="40" cy="60" r="14" fill="#ccfbf1" />
          <rect x="22" y="74" width="36" height="60" rx="10" fill="#ccfbf1" />
        </svg>

        {/* hotspots */}
        {beat.spots.map((s, i) => {
          const on = sel.has(i);
          let cls = "bg-white/90 text-slate-700 border-white/0 hover:bg-white";
          if (checked) {
            if (s.flag) cls = "bg-emerald-500 text-white";
            else if (on) cls = "bg-rose-500 text-white";
            else cls = "bg-white/60 text-slate-500";
          } else if (on) cls = "bg-amber-400 text-slate-900 ring-2 ring-white";
          return (
            <button
              key={i}
              type="button"
              onClick={() => toggle(i)}
              data-testid={`${beat.key}-spot-${i}`}
              className={`absolute -translate-x-1/2 -translate-y-1/2 text-[10px] sm:text-[11px] font-semibold rounded-full px-2 py-1 shadow transition-all leading-tight max-w-[42%] text-left ${cls}`}
              style={{ top: s.top, left: s.left }}
            >
              {checked && (s.flag ? <Check className="w-3 h-3 inline mr-0.5 -mt-0.5" /> : on ? <X className="w-3 h-3 inline mr-0.5 -mt-0.5" /> : null)}
              {s.t}
            </button>
          );
        })}
      </div>
      <p className="text-[11px] text-slate-500">Tap each action that breaks the sterile field.</p>

      {checked && (
        <div className="mt-1 space-y-1.5" data-testid={`${beat.key}-coaching`}>
          {beat.spots.filter((x) => x.flag).map((x, i) => (
            <div key={i} className="text-[11px] text-slate-600 leading-snug">
              <span className="text-emerald-600 font-semibold">⚑ {x.t}:</span> {x.coach}
            </div>
          ))}
        </div>
      )}
    </BeatShell>
  );
}
